import { useCachedUser } from '@hb/react'
import {
  FileDBValue,
  InputTemplateAttachment,
  SignableDocument,
  TemplateAttachment,
  WithId,
  populateConsentFormField,
} from '@hb/shared'
import { PropsWithChildren, useMemo } from 'react'
import { FileValuePdfView } from '../../PdfView'
import { EditorSize } from '../../RichText'
import { OnFormSubmit } from '../../forms/FinalForm/types'

export const ConsentFormPdfView = ({
  consentForm,
  editorSize,
  children,
  onFormSubmit,
  withDownload,
}: PropsWithChildren<{
  consentForm: WithId<SignableDocument>
  editorSize: EditorSize
  onFormSubmit?: OnFormSubmit
  withDownload?: boolean
}>) => {
  const { externalPdf, signedStoragePath, signedOn, toBeSignedByUser: toBeSignedById } = consentForm
  const { data: toBeSignedByUser } = useCachedUser(toBeSignedById)
  const signedValue = useMemo<FileDBValue | null>(
    () =>
      signedStoragePath && signedOn
        ? { storagePath: signedStoragePath, type: 'application/pdf', uploadedOn: signedOn, id: '' }
        : null,
    [signedStoragePath, signedOn],
  )
  const populatedAttachments = useMemo(
    () =>
      toBeSignedByUser
        ? Object.entries(externalPdf?.attachments ?? {}).reduce<Record<string, TemplateAttachment>>(
            (acc, [key, attachment]) => {
              if (attachment.type === 'input') {
                return {
                  ...acc,
                  [key]: {
                    ...attachment,
                    field: populateConsentFormField(attachment.field, toBeSignedByUser),
                  } as InputTemplateAttachment,
                }
              }
              return {
                ...acc,
                [key]: attachment,
              }
            },
            {},
          )
        : (externalPdf?.attachments ?? {}),
    [externalPdf, toBeSignedByUser],
  )

  if (signedStoragePath) {
    return (
      <FileValuePdfView
        withDownload={withDownload}
        value={signedValue}
        height={editorSize.height}
        modalWidth={editorSize.modalWidth}
        document={consentForm}
        width={editorSize.width}>
        {children}
      </FileValuePdfView>
    )
  }

  return (
    <FileValuePdfView
      value={externalPdf?.baseFile ?? null}
      attachments={populatedAttachments}
      document={consentForm}
      withDownload={withDownload}
      modalWidth={editorSize.modalWidth}
      onFormSubmit={onFormSubmit}
      height={editorSize.height}
      width={editorSize.width}>
      {children}
    </FileValuePdfView>
  )
}
